import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);
const mm = gsap.matchMedia();

/**
 * Base JS
 */

// add a class as page is loading (helpful for adding transition properties via css)
document.body.classList.add("body--js-added");
window.addEventListener("load", () => document.body.classList.add("body--window-loaded"));

/**
 * Handle nav scrolling behavior
 * Adjust classes during scroll so we can style
 */

// add pinned class to site header
const siteHeader = document.querySelector(".site-header");
const siteHeaderPinnedThreshold = 20; // threshold in pixels from top that we'll switch header to pinned
ScrollTrigger.create({
    end: "999999px",
    toggleClass: "site-header--is-pinned",
    trigger: ".site-header",
    start: siteHeaderPinnedThreshold,
});

// add classes based on color of background
let currentHeaderClasses = [];
const sectionClassMap = {
    // map of section classes => classes that we want to add to the site header
    "has-background-gradient": "site-header--on-gradient",
    "has-primary-background-color": "site-header--on-primary",
    "has-neutral-background-color": "site-header--on-neutral",
    "has-white-background-color": "site-header--on-white",
};
const pageSections = gsap.utils.toArray([
    ".page-section--main > .wp-block-cover, .page-section--main > .wp-block-group",
]);
pageSections.forEach((pageSection) => {
    ScrollTrigger.create({
        onToggle: (self) => {
            if (!self.isActive) {
                return;
            }

            let newHeaderClasses = [];
            let relevantElement = self.trigger.classList.contains("wp-block-cover")
                ? self.trigger.firstElementChild
                : self.trigger;

            // if we don't have a background, then consider it a white background
            if (
                !relevantElement.classList.contains("has-background-dim") &&
                !relevantElement.classList.contains("has-background")
            ) {
                newHeaderClasses.push(sectionClassMap["has-white-background-color"]);

                // otherwise map relevant classes from section to classes that make sense for header
            } else {
                relevantElement.classList.forEach((sectionClass) => {
                    sectionClassMap[sectionClass] && newHeaderClasses.push(sectionClassMap[sectionClass]);
                });
            }

            siteHeader.classList.remove(...currentHeaderClasses);
            siteHeader.classList.add(...newHeaderClasses);
            currentHeaderClasses = newHeaderClasses;
        },
        start: "top top+=30px",
        trigger: pageSection,
    });
});

/**
 * Logo
 */

// handle logo animation when page starts with logo

if (document.body.classList.contains("body--starts-with-logo")) {
    // after initial animation plays, adjust state of logo block, so we know to show hover animation
    const playerContainer = document.querySelector(".block-logo");
    const initialAnimation = document.querySelector(".block-logo__animation--initial");
    initialAnimation.addEventListener(
        "complete",
        () => {
            document.querySelector(".site-header").classList.add("site-header--logo-animation-complete");
            playerContainer.closest(".wp-block-cover").classList.add("animation-complete");
            playerContainer.classList.add("block-logo--initial-animation-complete");
        },
        { once: true }
    );

    // play hover animation on hover
    const hoverAnimation = document.querySelector(".block-logo__animation--hover");
    hoverAnimation.addEventListener("mouseover", () => {
        hoverAnimation.setDirection(1);
        hoverAnimation.play();
    });
    hoverAnimation.addEventListener("mouseleave", () => {
        hoverAnimation.setDirection(-1);
        hoverAnimation.play();
    });

    ScrollTrigger.create({
        end: "999999px",
        toggleClass: {
            targets: siteHeader,
            className: "site-header--scrolled-past-logo",
        },
        trigger: ".page-section--main > .wp-block-cover:first-child",
        start: (self) => {
            let bodyFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
            let sectionPadding = 155 * (bodyFontSize / 18);
            return `bottom top+=${sectionPadding}`;
        },
    });
}

/**
 * Animation
 */

// the below approach assumes:
// sections get an "animate-section" class, so that they can trigger animations
// elements within that section get both:
//     an animation class (e.g. "animate-in-right", "animate-in-fade", etc.) to control what animation is used
//     an order class (e.g. "animate-order-1", "animate-order-2", etc.) to control when the animation occurs

// get sections that we will use as triggers
const sectionsToAnimate = gsap.utils.toArray([".page-section--main > .animate-section"]);

// possible classes that can be added to elements and associated animations
const animations = {
    "animate-in-right": {
        opacity: 0,
        x: -50,
    },
    "animate-in-right-stagger": {
        opacity: 0,
        stagger: 0.05,
        x: -50,
    },
    "animate-in-down": {
        opacity: 0,
        y: -50,
    },
    "animate-in-down-stagger": {
        opacity: 0,
        stagger: 0.05,
        y: -50,
    },
    "animate-in-up": {
        opacity: 0,
        y: 50,
    },
    "animate-in-left": {
        opacity: 0,
        x: 50,
    },
    "animate-in-left-stagger": {
        opacity: 0,
        stagger: 0.05,
        x: 50,
    },
    "animate-in-fade": {
        opacity: 0,
    },
};

// classes that can be added to determine animation order
// this allows us to stagger animations in each section
const animationOrderClasses = ["animate-order-1", "animate-order-2", "animate-order-3"];

// settings that each order class gets in timeline
const animationOrderSettings = {
    1: "",
    2: ">+0.25s",
    3: ">+0.25s",
};

// set up triggers and timelines
sectionsToAnimate.forEach((sectionToAnimate) => {
    // assign timeline to scrolltrigger
    sectionToAnimate.tl = gsap.timeline({
        defaults: {
            duration: 0.25,
            ease: "ease-out",
        },
        onComplete: () => sectionToAnimate.classList.add("animation-complete"),
        scrollTrigger: {
            trigger: sectionToAnimate,
            start: "top center",
        },
    });

    // adds animation to timeline for given element with given order
    let assignAnimation = (elementToAnimate, order) => {
        for (let [className, animation] of Object.entries(animations)) {
            // for staggered animations, assume animation was added to parent which means we need to add to each child
            if (
                (className == "animate-in-right-stagger" ||
                    className == "animate-in-down-stagger" ||
                    className == "animate-in-left-stagger") &&
                elementToAnimate.classList.contains(className)
            ) {
                sectionToAnimate.tl.from(elementToAnimate.children, animation, animationOrderSettings[order]);
            } else {
                elementToAnimate.classList.contains(className) &&
                    sectionToAnimate.tl.from(elementToAnimate, animation, animationOrderSettings[order]);
            }
        }
    };

    // find elements to assign animations to
    animationOrderClasses.forEach((orderClass, i) => {
        gsap.utils
            .toArray(`.${orderClass}`, sectionToAnimate)
            .forEach((elementToAnimate) => assignAnimation(elementToAnimate, i + 1));
    });
});

/**
 * Stats
 */

import { CountUp } from "countup.js";
const countUpElements = gsap.utils.toArray([".animate-count-up"]);
if (countUpElements.length > 0) {
    countUpElements.forEach((countUpElement) => {
        new CountUp(countUpElement, countUpElement.dataset.end, {
            enableScrollSpy: true,
            scrollSpyOnce: true,
        }).start();
    });
}

/**
 * Award Listing
 */

// pin summaries when scrolling

const awardGroups = gsap.utils.toArray(".block-award-listing__award-group");
if (awardGroups.length > 0) {
    // only do this for desktop
    mm.add("(min-width: 768px)", () => {
        let awardGroups = gsap.utils.toArray(".block-award-listing__award-group");
        awardGroups.forEach((awardGroup) => {
            awardGroup.summaryContent = awardGroup.querySelector(".block-award-listing__award-group-summary-content");
            gsap.to(awardGroup.summaryContent, {
                opacity: 1,
                scrollTrigger: {
                    end: "bottom bottom",
                    toggleActions: "play reverse play reverse",
                    toggleClass: "block-award-listing__award-group--in-view",
                    trigger: awardGroup,
                    start: "top center",
                },
            });
        });
    });
}
